import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useEffect, useMemo, useState } from "react";

const TableLicensesAgent = ({ data, search, loading, tableHeight = 520 }) => {
  const [rows, setRows] = useState([]);
  // Set of row keys whose secret is currently visible
  const [visibleSecrets, setVisibleSecrets] = useState(new Set());

  useEffect(() => {
    if (!Array.isArray(data)) {
      setRows([]);
      return;
    }

    if (!search?.trim()) {
      setRows(data);
      return;
    }

    const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(escaped, "i");

    setRows(
      data.filter(
        (item) =>
          regex.test(item.codiceAttivazione ?? "") ||
          regex.test(String(item.device_limit ?? "")) ||
          regex.test(String(item.devices_slots_used ?? "")) ||
          regex.test(item.scrt ?? "")
      )
    );
  }, [search, data]);

  // Reset visibility when data changes
  useEffect(() => {
    setVisibleSecrets(new Set());
  }, [data]);

  const toggleSecret = (key) => {
    setVisibleSecrets((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const emptyLabel = useMemo(() => {
    if (loading) return "Loading...";
    return "No data";
  }, [loading]);

  const formatDate = (date) => {
    if (!date) return "-";
    try {
      return new Date(date).toLocaleDateString();
    } catch {
      return "-";
    }
  };

  return (
    <TableContainer
      sx={{
        height: tableHeight,
        minHeight: 0,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "background.paper",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Table stickyHeader sx={tableStyle}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: "30%" }}>Activation Code</TableCell>
            <TableCell sx={{ width: "15%" }}>Slots</TableCell>
            <TableCell sx={{ width: "20%" }}>Expiration</TableCell>
            <TableCell sx={{ width: "20%" }}>Secret</TableCell>
            <TableCell sx={{ width: "15%" }}>Status</TableCell>
          </TableRow>
        </TableHead>

        <TableBody key={search}>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>
                <Box
                  sx={{
                    height: 220,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    opacity: 0.85,
                  }}
                >
                  {loading && <CircularProgress size={18} />}
                  <Typography textAlign="center">{emptyLabel}</Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((item, idx) => {
              const rowKey = item.codiceAttivazione ?? `${item.device_limit}-${idx}`;
              const isVisible = visibleSecrets.has(rowKey);

              return (
                <TableRow key={rowKey}>
                  {/* codiceAttivazione */}
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      {item.codiceAttivazione ?? "-"}
                    </Typography>
                  </TableCell>

                  {/* devices_slots_used / device_limit */}
                  <TableCell>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {item.devices_slots_used ?? 0} / {item.device_limit ?? 0}
                    </Typography>
                  </TableCell>

                  {/* dataScadenza */}
                  <TableCell>
                    <Typography sx={{ fontSize: 14, opacity: 0.9 }}>
                      {formatDate(item.dataScadenza)}
                    </Typography>
                  </TableCell>

                  {/* scrt — masked with toggle */}
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {item.scrt && (
                        <Tooltip title={isVisible ? "Hide" : "Show"}>
                          <IconButton
                            size="small"
                            onClick={() => toggleSecret(rowKey)}
                            sx={{
                              opacity: 0.55,
                              flexShrink: 0,
                              "&:hover": { opacity: 1 },
                              transition: "opacity .15s ease",
                            }}
                          >
                            {isVisible ? (
                              <VisibilityOffIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}

                      <Typography
                        sx={{
                          fontSize: 13,
                          opacity: 0.85,
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                          flex: 1,
                          transition: "opacity .15s ease",
                        }}
                      >
                        {item.scrt
                          ? isVisible
                            ? item.scrt
                            : "••••••••"
                          : "-"}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* activated */}
                  <TableCell>
                    <Chip
                      label={item.activated ? "Active" : "Inactive"}
                      size="small"
                      color={item.activated ? "success" : "default"}
                      variant={item.activated ? "filled" : "outlined"}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableLicensesAgent;

const tableStyle = {
  ".MuiTableCell-root": {
    fontSize: 14,
    borderBottom: "1px solid",
    borderColor: "divider",
    py: 1.25,
  },

  ".MuiTableCell-head": {
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontSize: 12,
    backgroundColor: "background.paper",
    borderBottom: "1px solid",
    borderColor: "divider",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },

  ".MuiTableCell-body": {
    fontWeight: 400,
  },

  ".MuiTableRow-root": {
    transition: "background-color .15s ease",
    "&:nth-of-type(even) .MuiTableCell-body": {
      backgroundImage:
        "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0))",
    },
    "&:hover .MuiTableCell-body": {
      backgroundColor: "action.hover",
    },
  },
};